import React from 'react';
import { Box, Card, Flex, FlexProps, Text } from '@opendoor/bricks/core';
import { InternalLink, InternalLinkProps } from 'platform/react-router';
import { CardProps } from '@opendoor/bricks/core/Card';

export interface ISection {
  /**
   * Title of the section
   *
   * @type {string}
   * @memberof ISection
   */
  title?: string;

  /**
   * Content of the section
   *
   * @type {JSX.Element}
   * @memberof ISection
   */
  content: JSX.Element;

  /**
   * Bottom divider shown by default
   */
  hideDivider?: boolean;
}

export type IModuleCardProps = CardProps & {
  /** Accessible name to give to this card */
  ariaLabel: string;

  preTitle?: JSX.Element;

  /**
   * Title of the module
   *
   * @type {string}
   * @memberof IModuleCardProps
   */
  title?: string;

  /**
   * Sections of content, will automatically be separated by a grey separator
   *
   * @type {Array<JSX.Element>}
   * @memberof IModuleCardProps
   */
  sections: Array<ISection>;
};

// component-test-check-ignore
/** TODO: Add documentation */
export const ModuleCard: React.FC<IModuleCardProps> = ({
  ariaLabel,
  title,
  sections,
  preTitle,
  ...props
}) => {
  return (
    <Card
      variant="tertiary"
      _hover={{}} // Overriding the default 'hover' state for the tertiary variant
      _active={{}} // Overriding the default 'active' state for the tertiary variant
      mb={[0, null, 7, 5]}
      role="group"
      aria-label={ariaLabel}
      {...props}
    >
      <Flex flexDirection="column" width="100%">
        {preTitle && <Box mb={6}>{preTitle}</Box>}
        {title && (
          <Text
            as="h2"
            data-testid={`${title}-module-header`}
            mb={7}
            fontWeight="bold"
            fontSize={['s2', 's3', 's4']}
          >
            {title}
          </Text>
        )}
        {sections.map((section, index) => (
          <Box key={index}>
            {section.title && (
              <Text
                data-testid={section.title}
                mb={5}
                color="neutrals90"
                fontWeight="semibold"
                size="s1"
                fontSize={['s0', 's1']}
              >
                {section.title}
              </Text>
            )}
            {section.content}
            {index !== sections.length - 1 && !section.hideDivider && (
              <Flex width="100%" backgroundColor="neutrals30" height={1} mt={3} mb={8} />
            )}
          </Box>
        ))}
      </Flex>
    </Card>
  );
};

/**
 * Informational content section used in ModuleCards
 */
export const DataBlock: React.FC<
  Omit<FlexProps, 'onClick'> & {
    testId?: string;
    label: string;
    content: string | JSX.Element;
    eventName?: string;
    to?: string;
    onClick?: InternalLinkProps['onClick'];
    ariaLabel?: string;
  }
> = ({ testId, label, content, to, eventName, onClick, ariaLabel, ...props }) => {
  return (
    <Flex
      {...props}
      width={['100%', 'auto']}
      flexDirection="column"
      justifyContent="center"
      alignItems="start"
    >
      <Text color="neutrals90" fontSize={['s0', 's1', null, 's2']}>
        {label}
      </Text>
      {eventName && to ? (
        <InternalLink
          data-testid={testId || `data-block-content--${label}`}
          analyticsName={eventName}
          fontWeight="semibold"
          fontSize={['s0', 's1', 's2']}
          lineHeight={['s0', 's1', 's2']}
          onClick={onClick}
          to={to}
          aria-label={ariaLabel ? ariaLabel : null}
        >
          {content}
        </InternalLink>
      ) : (
        <Text
          data-testid={testId || `data-block-content--${label}`}
          color={props.color ?? 'neutrals100'}
          fontSize={['s0', 's1', 's2']}
          fontWeight="semibold"
        >
          {content}
        </Text>
      )}
    </Flex>
  );
};
